import { useEffect, useState } from "react";
import { Modal, Button, Toast, Card, Pagination } from "react-bootstrap";
import Header from "../../components/Header";
import NavBar from "../../components/NavBar";
import Footer from "../../components/Footer";
import styles from "./Solicitudes.module.css";
import { Request } from "../../types/interfaces";
import { useAppContext } from "../../components/AppContext";
import { request } from "http";
import { useLoading } from "../../components/LoadingContext";

function Solicitudes() {
  const { setLoading } = useLoading();
  const { setRequestsCount } = useAppContext();
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState(1);
  const [changesRequests, setChangesRequests] = useState<Request[]>([]);
  const [selectedRequest, setSelectedRequest] = useState<Request | null>(null);
  const [selectedRequestId, setSelectedRequestId] = useState<number | null>(
    null
  );
  const [showModal, setShowModal] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [toastVariant, setToastVariant] = useState<"success" | "danger">(
    "success"
  );

  const fetchChangesRequests = async (page = 1) => {
    setLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/getChangesRequests?page=${page}&limit=10`, { method: "GET", headers: {"Content-Type": "application/json",}, credentials: "include" });
      if (response.ok) {
        const { data, totalPages: tp } = await response.json();
        setChangesRequests(data);
        setTotalPages(tp);
      } else console.error("Error al obtener solicitudes");
    } catch (e) {
      console.error("Error de red", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchChangesRequests(currentPage);
  }, [currentPage]);

  const handleUpdateRequest = async (id: number, status: string) => {
    setLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/requests/${id}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ estado: status }),
        credentials: "include"
      });

      if (response.ok) {
        const updatedRequest = await response.json();
        setChangesRequests((prevRequests) =>
          prevRequests.filter(
            (request) =>
              request.id !== updatedRequest.id ||
              updatedRequest.estado === "Pendiente"
          )
        );
        setToastVariant(status === "Aprobada" ? "success" : "danger");
        setToastMessage(
          status === "Aprobada" ? "Solicitud aceptada" : "Solicitud rechazada"
        );
        setShowToast(true);
        setShowModal(false);

        await fetchRequestsCount();
      } else {
        console.error("Error al actualizar la solicitud");
      }
    } catch (error) {
      console.error("Error de red al actualizar la solicitud", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchRequestsCount = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/admin/getRequestsCount`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        });
      if (response.ok) {
        const data = await response.json();
        setRequestsCount(data.count);
      } else {
        console.error("Error al obtener el número de solicitudes");
      }
    } catch (error) {
      console.error("Error de red al obtener el número de solicitudes", error);
    } finally {
      setLoading(false);
    }
  };

  const handleShowModal = async (request: Request) => {
    setSelectedRequestId(request.id);
    setShowModal(true);
    setLoading(true);

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/admin/getChangesRequests/${request.id}`, { method: "GET", headers: {"Content-Type": "application/json",}, credentials: "include" }
      );

      if (response.ok) {
        const data = await response.json();

        if (data) {
          setSelectedRequest(data); 
        } else {
          console.error("La respuesta está vacía");
        }
      } else {
        console.error("Error al obtener los detalles de la solicitud");
      }
    } catch (error) {
      console.error("Error de red al obtener los detalles de la solicitud", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedRequestId(null);
    setSelectedRequest(null);
  };

  const paginate = (pageNumber: number) => setCurrentPage(pageNumber);

  const paginationControls = (
    <div className={styles.paginationControls}>
      <Pagination>
        <Pagination.First
          onClick={() => paginate(1)}
          disabled={currentPage === 1}
        />
        <Pagination.Prev
          onClick={() => paginate(currentPage - 1)}
          disabled={currentPage === 1}
        />
        <Pagination.Item
          disabled
        >{`Página ${currentPage} de ${totalPages}`}</Pagination.Item>
        <Pagination.Next
          onClick={() => paginate(currentPage + 1)}
          disabled={currentPage === totalPages}
        />
        <Pagination.Last
          onClick={() => paginate(totalPages)}
          disabled={currentPage === totalPages}
        />
      </Pagination>
    </div>
  );

  return (
    <>
      <Header />
      <NavBar />
      <div className={styles.requestsContainer}>
        <h2 className={styles.titlesRoutes}>Solicitudes</h2>
        <Card className={`mb-2 ${styles.card}`}>
          {changesRequests.length === 0 ? (
            <div className={styles.noRequestsMessage}>
              No hay solicitudes de cambios pendientes.
            </div>
          ) : (
            changesRequests.map((request) => (
              <section className={styles.requestsSection} key={request.id}>
                <div>
                  <div
                    className={`${styles.requestsDataContainer} ${
                      selectedRequestId === request.id
                        ? styles.selectedRequest
                        : ""
                    }`}
                  >
                    <div
                      className={styles.request}
                      onClick={() => handleShowModal(request)}
                    >
                      <div className={styles.requestHeader}>
                        <h3># {request.id}</h3>
                        <p className={styles.requestStatus}>{request.estado}</p>
                      </div>
                      <p className={styles.requestFunctionary}>
                        {request.funcionario.funcionario}
                      </p>
                      <p className={styles.requestCreYAct}>
                        <strong>Creada:</strong>{" "}
                        {new Date(request.creado_el).toLocaleString()}
                      </p>
                      <p className={styles.requestCreYAct}>
                        <strong>Actualizada:</strong>{" "}
                        {new Date(request.actualizado_el).toLocaleString()}
                      </p>
                    </div>
                  </div>
                </div>
              </section>
            ))
          )}

          <Toast
            onClose={() => setShowToast(false)}
            show={showToast}
            delay={3000}
            autohide
            className={`${styles.toastCenter} ${
              toastVariant === "success"
                ? "bg-success text-white"
                : "bg-danger text-white"
            }`}
          >
            <Toast.Body>{toastMessage}</Toast.Body>
          </Toast>
        </Card>
      </div>

      {paginationControls}

      {/* Modal para mostrar los detalles de la solicitud */}
      {selectedRequest && (
        <Modal
          show={showModal}
          onHide={handleCloseModal}
          dialogClassName="modal-90w"
        >
          <Modal.Header closeButton className={styles.modalHeader}>
            <Modal.Title className={styles.modalTitle}>
              # {selectedRequest.id} - Detalles de la Solicitud
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className={styles.modalBody}>
            <section className={styles.sectionDatosActuales}>
              <h4>Datos Actuales</h4>
              <p>
                <strong>Nombre(s) y apellido(s):</strong>{" "}
                {selectedRequest.funcionario.funcionario}
              </p>
              <p>
                <strong>Orgánica:</strong>{" "}
                {selectedRequest.funcionario.organica?.nombre ||
                  "No disponible"}
              </p>
              <p>
                <strong>Establecimiento:</strong>{" "}
                {selectedRequest.funcionario.establecimiento?.nombre ||
                  "No disponible"}
              </p>
              <p>
                <strong>Ubicación adicional:</strong>{" "}
                {selectedRequest.funcionario.ubi_adicional || "No disponible"}
              </p>
              <p>
                <strong>Interno:</strong>{" "}
                {selectedRequest.funcionario.interno || "No disponible"}
              </p>
              <p>
                <strong>Usuario GDE:</strong>{" "}
                {selectedRequest.funcionario.usuario_gde || "No disponible"}
              </p>
              <p>
                <strong>Repartición GDE:</strong>{" "}
                {selectedRequest.funcionario.reparticion_gde?.nombre ||
                  "No disponible"}
              </p>
              <p>
                <strong>Sector GDE:</strong>{" "}
                {selectedRequest.funcionario.sector_gde?.nombre ||
                  "No disponible"}
              </p>
            </section>

            <section className={styles.sectionDatosSolicitados}>
              <h4>Datos Solicitados</h4>
              {(() => {
                try {
                  const cambios = JSON.parse(
                    selectedRequest.cambios_solicitados
                  );
                  return (
                    <>
                      <p>
                        <strong>Nombre(s) y apellido(s):</strong>{" "}
                        {cambios.funcionario || "No disponible"}
                      </p>
                      <p>
                        <strong>Orgánica:</strong>{" "}
                        {cambios.organica?.nombre || "No disponible"}
                      </p>
                      <p>
                        <strong>Establecimiento:</strong>{" "}
                        {cambios.establecimiento?.nombre || "No disponible"}
                      </p>
                      <p>
                        <strong>Ubicación adicional:</strong>{" "}
                        {cambios.ubi_adicional || "No disponible"}
                      </p>
                      <p>
                        <strong>Interno:</strong>{" "}
                        {cambios.interno || "No disponible"}
                      </p>
                      <p>
                        <strong>Usuario GDE:</strong>{" "}
                        {cambios.usuario_gde || "No disponible"}
                      </p>
                      <p>
                        <strong>Repartición GDE:</strong>{" "}
                        {cambios.reparticion_gde?.nombre || "No disponible"}
                      </p>
                      <p>
                        <strong>Sector GDE:</strong>{" "}
                        {cambios.sector_gde?.nombre || "No disponible"}
                      </p>
                    </>
                  );
                } catch (e) {
                  console.error("Error al analizar los cambios solicitados", e);
                  return <p>Error al procesar los cambios solicitados.</p>;
                }
              })()}
            </section>
          </Modal.Body>
          <Modal.Footer className={styles.modalFooter}>
            <Button
              variant="primary"
              onClick={() =>
                selectedRequest &&
                handleUpdateRequest(selectedRequest.id, "Aprobada")
              }
            >
              Aceptar
            </Button>
            <Button
              variant="danger"
              onClick={() =>
                selectedRequest &&
                handleUpdateRequest(selectedRequest.id, "Rechazada")
              }
            >
              Rechazar
            </Button>
          </Modal.Footer>
        </Modal>
      )}
      <Footer />
    </>
  );
}

export default Solicitudes;
